import { z } from 'zod'

// ─── Price alerts contract (/v2/price-alerts) ───
//
// Wire shapes from edge-financial-api's price-alerts router (edge-streaming's
// pricealerts consumer fires them). Plain JSON on success, the app envelope
// `{success:false, error:{code,message}}` on errors.

export const PRICE_ALERT_SESSIONS = ['overnight', 'premarket', 'regular', 'afterhours'] as const
export const PriceAlertSessionSchema = z.enum(PRICE_ALERT_SESSIONS)
export type PriceAlertSession = z.infer<typeof PriceAlertSessionSchema>

export const PriceAlertDirectionSchema = z.enum(['above', 'below'])
export type PriceAlertDirection = z.infer<typeof PriceAlertDirectionSchema>

export const PriceAlertStatusSchema = z.enum(['active', 'triggered', 'cancelled', 'expired'])
export type PriceAlertStatus = z.infer<typeof PriceAlertStatusSchema>

export const PriceAlertStatusFilterSchema = z.enum([
  'active',
  'triggered',
  'cancelled',
  'expired',
  'all',
])
export type PriceAlertStatusFilter = z.infer<typeof PriceAlertStatusFilterSchema>

/** Postgres NUMERIC can serialize as a string; accept both. */
const num = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() !== '' ? Number(v) : v),
  z.number().finite(),
)
const optNum = num.nullable().default(null)
const optStr = z.string().nullable().default(null)

export const PriceAlertSchema = z.object({
  id: z.coerce.string(),
  symbol: z.string(),
  direction: PriceAlertDirectionSchema,
  level: num,
  original_level: optNum,
  sessions: z.array(z.string()).default([...PRICE_ALERT_SESSIONS]),
  status: PriceAlertStatusSchema,
  note: optStr,
  ref_price: optNum,
  expires_at: optStr,
  created_at: z.string(),
  updated_at: optStr,
  triggered_at: optStr,
  trigger_price: optNum,
  trigger_session: optStr,
  gapped: z.boolean().nullable().default(null),
  adjusted_note: optStr,
  notified_at: optStr,
})
export type PriceAlert = z.infer<typeof PriceAlertSchema>

/** `limit` and `active` (unexpired active alerts) are null from a server that predates them. */
export const PriceAlertListSchema = z.object({
  alerts: z.array(PriceAlertSchema),
  limit: z.number().int().nullable().default(null),
  active: z.number().int().nullable().default(null),
})
export type PriceAlertList = z.infer<typeof PriceAlertListSchema>

export interface PriceAlertCreate {
  symbol: string
  direction: PriceAlertDirection
  level: number
  sessions?: PriceAlertSession[]
  note?: string
  ref_price?: number
  expires_at?: string
}

// ─── Realtime events (ch "user") ───

/** `type: "price_alert"`: an alert fired. */
export const PriceAlertFiredEventSchema = z.object({
  event_id: z.coerce.string(),
  alert_id: z.coerce.string(),
  symbol: z.string(),
  direction: PriceAlertDirectionSchema.or(z.string()),
  level: num,
  trigger_price: optNum,
  gapped: z.boolean().nullable().default(null),
  session: optStr,
  fired_at: optStr,
  message: optStr,
})
export type PriceAlertFiredEvent = z.infer<typeof PriceAlertFiredEventSchema>

/** `type: "price_alert_adjusted"`: a resting alert moved for a stock split. */
export const PriceAlertAdjustedEventSchema = z.object({
  event_id: z.coerce.string(),
  alert_id: z.coerce.string().nullable().default(null),
  symbol: z.string().nullable().default(null),
  level: optNum,
  old_level: optNum,
  split: z.unknown().optional(),
  execution_date: optStr,
  message: optStr,
})
export type PriceAlertAdjustedEvent = z.infer<typeof PriceAlertAdjustedEventSchema>
